import { Validators } from '@biteinc/common';
import { allEnumValues, CustomerIdentifier, CustomerIdentifierInputMethod, FulfillmentMethod, FulfillmentMethodHelper, MenuItemCategory, UserRight, } from '@biteinc/enums';
export const diningOptionSchema = {
    displayName: 'dining option',
    type: 'object',
    fields: {
        fulfillmentMethod: {
            type: 'int',
            values: allEnumValues(FulfillmentMethod),
            required: true,
            displayName: 'Type',
            isHidden: true,
        },
        name: {
            type: 'shortString',
            displayName: 'Name (optional)',
            validationRegex: '^[a-zA-Z0-9\\s\\-\\.]{1,16}$',
            tooltip: {
                text: 'Max length 16; only letters and numbers can be used.',
            },
            canHaveLocalizationOverrides: true,
            // '16 char limit: to fit on the screen; char limitations: no special characters in case we need to print it'
        },
        isEnabled: {
            type: 'bool',
            displayName: 'Enabled',
            writeRight: UserRight.ToggleDiningOptions,
        },
        forceGuestToSelectIfSingleDiningOption: {
            type: 'bool',
            displayName: 'Force Choice When Only Option Available',
            tooltip: {
                text: 'If this dining option is the only one in the menu, still force the user to choose their dining option (fulfillment method).',
            },
        },
        customerIdentifierOptions: {
            type: 'array',
            elementType: 'object',
            required: true,
            minCount: 1,
            displayName: 'Customer Identifiers',
            ui: 'accordion',
            uiSubtype: 'CustomerIdentifiers',
            fields: {
                customerIdentifier: {
                    type: 'int',
                    values: allEnumValues(CustomerIdentifier),
                    required: true,
                    isHidden: true,
                },
                inputMethod: {
                    type: 'int',
                    values: allEnumValues(CustomerIdentifierInputMethod),
                    required: true,
                    displayName: 'Input Method',
                    ui: 'dropdown',
                },
                askAtBeginning: {
                    type: 'bool',
                    displayName: 'Ask at the beginning?',
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [
                                CustomerIdentifier.Address,
                                CustomerIdentifier.GuestName,
                                CustomerIdentifier.PhoneNumber,
                                CustomerIdentifier.TableNumber,
                                CustomerIdentifier.VehicleDescription,
                            ],
                        },
                    ],
                },
                subStringLength: {
                    type: 'int',
                    displayName: 'Substring length',
                    tooltip: {
                        text: 'Enter a positive number to take the first X characters; a negative one to take the last X characters',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.PosOrderNumber],
                        },
                    ],
                },
                numberOfDigits: {
                    type: 'int',
                    displayName: 'Number of digits',
                    min: 1,
                    max: 4,
                    ui: 'radiolist',
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.TableNumber],
                        },
                    ],
                },
                isOptional: {
                    type: 'bool',
                    displayName: 'Is Optional',
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [
                                CustomerIdentifier.GuestName,
                                CustomerIdentifier.PhoneNumber,
                                CustomerIdentifier.GuestEmail,
                            ],
                        },
                    ],
                },
                autoFillFromUrl: {
                    type: 'bool',
                    displayName: 'Auto Fill Table Number from URL',
                    tooltip: {
                        text: 'This will only work for Flash. If the url contains the table number, the customer identifier step will be skipped.',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.TableNumber],
                        },
                    ],
                },
                enforceLastInitial: {
                    type: 'bool',
                    displayName: 'Enforce Last Initial',
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.GuestName],
                        },
                    ],
                },
                adaPrefilledValue: {
                    type: 'shortString',
                    displayName: 'ADA Prefilled Value',
                    tooltip: {
                        text: 'This value will be pre-filled when Storm pad is connected.',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.GuestName, CustomerIdentifier.TableNumber],
                        },
                    ],
                },
                scannedOnlyAutofillValue: {
                    type: 'shortString',
                    displayName: 'Scanned-Only Order Autofill Value',
                    tooltip: {
                        text: 'When every item in the cart was scanned, the kiosk does not ask for the guest name, even if Always Prompt Guest Name at Checkout is on. It sets the guest name to this value.',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.GuestName],
                        },
                    ],
                },
                askWithLoyalty: {
                    type: 'bool',
                    displayName: 'Ask with Loyalty',
                    exclusiveTo: 'vitrine',
                    tooltip: {
                        text: 'If Loyalty is enabled, ask the guest to sign in with their loyalty account to get their name. Only visible during checkout.',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.GuestName],
                        },
                    ],
                },
                instructionalImage: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    displayName: 'Instructional Image',
                    keepAspectRatio: 'contain',
                    size: {
                        width: 480,
                        height: 480,
                    },
                    tooltip: {
                        text: 'Visual aid/reference for table number.',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.TableNumber],
                        },
                    ],
                },
                skipIfCartOnlyContainsCategories: {
                    type: 'array',
                    elementType: 'int',
                    elementValues: allEnumValues(MenuItemCategory),
                    displayName: 'Item Categories to Skip If Cart Only Contains',
                    ui: 'table',
                    placeholder: 'Select Item Categories',
                    exclusiveTo: 'vitrine',
                    tooltip: {
                        text: 'If the order contains ONLY items in the selected categories, the table tracker will not be dispensed.',
                    },
                    conditions: [
                        {
                            field: 'customerIdentifier',
                            values: [CustomerIdentifier.TableNumber],
                        },
                    ],
                },
            },
        },
        outposts: {
            type: 'array',
            elementType: 'object',
            minCount: 1,
            displayName: 'Outposts',
            ui: 'accordion',
            uiSubtype: 'OutpostLocations',
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return FulfillmentMethodHelper.isAnOutpost(fulfillmentMethod);
                    }),
                },
            ],
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                    isHidden: true,
                },
                name: {
                    displayName: 'Display Name',
                    type: 'shortString',
                    required: true,
                    tooltip: {
                        text: 'Something like Building B Lobby.',
                    },
                },
                instructionalImage: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    displayName: 'Instructional Image',
                    keepAspectRatio: 'contain',
                    imagePrefix: 'outpost',
                    size: {
                        width: 320,
                        height: 420,
                    },
                    tooltip: {
                        text: 'Visual aid/reference for outpost location.',
                    },
                },
                pickupInstructions: {
                    displayName: 'Pick Up Instructions',
                    type: 'shortString',
                    required: true,
                    canHaveLocalizationOverrides: true,
                },
                requireRoomNumber: {
                    displayName: 'Require room number',
                    type: 'bool',
                },
                pickupTimeSlots: {
                    type: 'array',
                    displayName: 'Specific Pick Up Time Slots',
                    elementType: 'object',
                    ui: 'accordion',
                    uiSubtype: 'PickupTimeSlots',
                    fields: {
                        day: {
                            type: 'int',
                            required: true,
                            isHidden: true,
                        },
                        timeSlots: {
                            displayName: 'Time Slots',
                            type: 'array',
                            elementType: 'dayTimestamp',
                            required: true,
                            minCount: 1,
                            ui: 'table',
                            placeholder: 'Select a Time Slot',
                        },
                    },
                },
            },
        },
        deliveryAreaRadius: {
            type: 'int',
            displayName: 'Delivery Area Radius (Miles)',
            tooltip: {
                text: 'Set the delivery area radius to ensure guests outside of this cannot place delivery orders.',
            },
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return FulfillmentMethodHelper.isDelivery(fulfillmentMethod);
                    }),
                },
            ],
        },
        deliveryTimeEstimate: {
            type: 'int',
            min: 1,
            max: 600,
            displayName: 'Delivery time estimate',
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return FulfillmentMethodHelper.isDelivery(fulfillmentMethod);
                    }),
                },
            ],
            tooltip: {
                text: 'An estimated delivery time that will automatically be added to the initial lead time estimate.',
            },
        },
        // Maintain tokens used in first and last name templates in locations_validation under 'allowedNameTemplateTokens'
        orderFirstNameTemplate: {
            type: 'keyString',
            displayName: 'First Name Label Template',
            validationRegex: '^(([a-zA-Z0-9\\-\\s\\.#:\\[\\]]+)|(?:{{{?(\\/[a-z]+)?(#[a-z]+)?[a-zA-Z_\\s]*}?}}))+$',
            tooltip: {
                text: 'Consult eng for help setting this up. Available tokens: ORDER_NUMBER, ORDER_TYPE, GUEST_NAME, GUEST_PHONE_NUMBER, TABLE_NUMBER, OUTPOST_NAME, IS_CASH, KIOSK_NAME',
            },
        },
        // Maintain tokens used in first and last name templates in locations_validation under 'allowedNameTemplateTokens'
        orderLastNameTemplate: {
            type: 'keyString',
            displayName: 'Last Name Label Template',
            validationRegex: '^(([a-zA-Z0-9\\-\\s\\.#:\\[\\]]+)|(?:{{{?(\\/[a-z]+)?(#[a-z]+)?[a-zA-Z_\\s]*}?}}))+$',
            tooltip: {
                text: 'Consult eng for help setting this up. Available tokens: ORDER_NUMBER, ORDER_TYPE, GUEST_NAME, GUEST_PHONE_NUMBER, TABLE_NUMBER, OUTPOST_NAME, IS_CASH, KIOSK_NAME',
            },
        },
        printerReceiptFulfillmentTemplate: {
            type: 'codeString',
            displayName: 'Printer Receipt Fulfillment Template',
            tooltip: {
                text: 'Available tokens: ORDER_NUMBER, GUEST_NAME, GUEST_PHONE_NUMBER, TABLE_NUMBER, LOCATION_NAME, LOCATION_PHONE_NUMBER, LOCATION_ADDRESS, ORG_NAME, KIOSK_NAME; Available tags: <b> for bold, <s1>,<s2>,<s3> for text size.',
            },
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return FulfillmentMethodHelper.isKiosk(fulfillmentMethod);
                    }),
                },
            ],
        },
        emailReceiptFulfillmentTemplate: {
            type: 'codeString',
            displayName: 'Email Receipt Fulfillment Template',
            tooltip: {
                text: 'Available tokens: ORDER_NUMBER, GUEST_NAME, GUEST_PHONE_NUMBER, TABLE_NUMBER, VEHICLE_DESCRIPTION, LOCATION_NAME, LOCATION_PHONE_NUMBER, LOCATION_ADDRESS, ORG_NAME, KIOSK_NAME',
            },
        },
        successScreenFulfillmentTemplate: {
            type: 'codeString',
            displayName: 'Success Screen Fulfillment Template',
            tooltip: {
                text: 'Available tokens: ORDER_NUMBER, GUEST_NAME, GUEST_PHONE_NUMBER, TABLE_NUMBER, VEHICLE_DESCRIPTION, LOCATION_NAME, LOCATION_PHONE_NUMBER, LOCATION_ADDRESS, ORG_NAME, KIOSK_NAME',
            },
            exclusiveTo: 'gcn',
        },
        orderStatusNotificationsEnabled: {
            type: 'bool',
            displayName: 'Enable order status notifications',
            tooltip: {
                text: 'Only available for select POS integrations',
            },
        },
        toGoBagItemId: {
            type: 'mongoId',
            displayName: 'Opt-in Bag Item',
            ui: 'dropdown',
            isNullable: true,
            tooltip: {
                text: 'Setting an item here will surface a pop up pre-checkout asking if the guest wants the item added to cart. Item to add to cart when if the "To Go Bag" option is accepted.',
            },
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return (FulfillmentMethodHelper.isToGo(fulfillmentMethod) &&
                            FulfillmentMethodHelper.isKiosk(fulfillmentMethod));
                    }),
                },
            ],
        },
        futureOrdersEnabled: {
            type: 'bool',
            displayName: 'Enable Future Orders',
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return ![FulfillmentMethod.KIOSK_DINE_IN, FulfillmentMethod.KIOSK_TO_GO].includes(fulfillmentMethod);
                    }),
                },
            ],
        },
        asapOrdersEnabled: {
            type: 'bool',
            displayName: 'Enable Asap Orders',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
        },
        skipIncludeUtensilsOnFlashOrders: {
            type: 'bool',
            displayName: 'Skip Utensils opt in on Flash orders',
            tooltip: {
                text: 'This overrides the location level opt in.',
            },
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return !FulfillmentMethodHelper.isKiosk(fulfillmentMethod);
                    }),
                },
            ],
        },
        skipIncludeUtensilsOnKioskOrders: {
            type: 'bool',
            displayName: 'Skip Utensils opt in on Kiosk orders',
            tooltip: {
                text: 'This overrides the location level opt in.',
            },
            conditions: [
                {
                    field: 'fulfillmentMethod',
                    values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
                        return FulfillmentMethodHelper.isKiosk(fulfillmentMethod);
                    }),
                },
            ],
        },
        calendarDaysAheadFutureOrders: {
            type: 'int',
            min: 0,
            max: 30,
            displayName: 'Calendar Days For Future Order Slots',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
        },
        businessDaysAheadFutureOrders: {
            type: 'int',
            min: 0,
            max: 30,
            displayName: 'Business Days For Future Order Slots',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
        },
        futureOrdersTimeIntervals: {
            type: 'int',
            min: 1,
            max: 120,
            displayName: 'Future Order Time Intervals (in minutes)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'For example, if set to 30 then available order times will be 1:00pm, 1:30pm, 2:00pm, etc.',
            },
        },
        futureOrdersFirstTimeIntervalBuffer: {
            type: 'int',
            min: 3,
            max: 4320, // 72 hours
            displayName: 'Minimum order lead time (minutes)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Minimum time buffer between now and first possible order time interval.',
            },
        },
        futureOrdersLastTimeIntervalBuffer: {
            type: 'int',
            min: 0,
            max: 1440, // 24 hours
            displayName: 'Maximum order lead time (minutes)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Maximum time buffer for the last possible time interval since the first one. Set to 0 to unset.',
            },
        },
        futureOrdersSendOrderBeforePickupAdvance: {
            type: 'int',
            min: 0,
            // 7 days = 168 hours = 10080 minutes
            max: 10080,
            displayName: 'Minutes before order pickup time to send order',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Max Value: 10,080 Minutes (7 Days)',
            },
        },
        smsNotificationEnabled: {
            type: 'bool',
            displayName: 'Enable pickup reminder SMS notifications',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
            ],
        },
        asapOrdersFirstSmsNotificationAfterSentEnabled: {
            type: 'bool',
            displayName: 'Enable first guest SMS notification after order is sent to POS (Asap orders)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
            ],
        },
        asapOrdersFirstSmsNotificationAfterSentDelay: {
            type: 'int',
            min: 0,
            max: 15,
            displayName: 'Minutes after the order is sent to send first SMS Notification (Asap orders)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersFirstSmsNotificationAfterSentEnabled',
                    values: [true],
                },
            ],
        },
        asapOrdersFirstSmsNotificationAfterSentMessageTemplate: {
            type: 'keyString',
            validationRegex: Validators.smsRegexString,
            displayName: 'First SMS Message after order is sent (Asap orders)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersFirstSmsNotificationAfterSentEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Consult eng for help setting this up. Available tokens: GUEST_NAME, ORDER_NUMBER, LOCATION_NAME, ORG_NAME, KIOSK_NAME',
            },
        },
        asapOrdersSecondSmsNotificationAfterSentEnabled: {
            type: 'bool',
            displayName: 'Enable second guest SMS notification after order is sent to POS (Asap orders)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
            ],
        },
        asapOrdersSecondSmsNotificationAfterSentDelay: {
            type: 'int',
            min: 0,
            max: 15,
            displayName: 'Minutes after the order is sent to send second SMS Notification (Asap orders)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersSecondSmsNotificationAfterSentEnabled',
                    values: [true],
                },
            ],
        },
        asapOrdersSecondSmsNotificationAfterSentMessageTemplate: {
            type: 'keyString',
            validationRegex: Validators.smsRegexString,
            displayName: 'Second SMS Message after order is sent (Asap orders)',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'asapOrdersSecondSmsNotificationAfterSentEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Consult eng for help setting this up. Available tokens: GUEST_NAME, ORDER_NUMBER, LOCATION_NAME, ORG_NAME, KIOSK_NAME',
            },
        },
        futureOrdersFirstSmsNotificationBeforePickupEnabled: {
            type: 'bool',
            displayName: 'Enable first guest SMS notification before order pickup time',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
            ],
        },
        futureOrdersFirstSmsNotificationBeforePickupAdvance: {
            type: 'int',
            min: 0,
            max: 60,
            displayName: 'Minutes before order pickup time to send First SMS Notification',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'futureOrdersFirstSmsNotificationBeforePickupEnabled',
                    values: [true],
                },
            ],
        },
        futureOrdersFirstSmsNotificationBeforePickupMessageTemplate: {
            type: 'keyString',
            validationRegex: Validators.smsRegexString,
            displayName: 'First SMS Message before order pickup time',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'futureOrdersFirstSmsNotificationBeforePickupEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Consult eng for help setting this up. Available tokens: GUEST_NAME, ORDER_NUMBER, LOCATION_NAME, ORG_NAME, ORDER_PICKUP_TIME, OUTPOST_NAME, KIOSK_NAME',
            },
        },
        futureOrdersSecondSmsNotificationBeforePickupEnabled: {
            type: 'bool',
            displayName: 'Enable second guest SMS notification before order pickup time',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
            ],
        },
        futureOrdersSecondSmsNotificationBeforePickupAdvance: {
            type: 'int',
            min: 0,
            max: 60,
            displayName: 'Minutes before order pickup time to send second SMS Notification',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'futureOrdersSecondSmsNotificationBeforePickupEnabled',
                    values: [true],
                },
            ],
        },
        futureOrdersSecondSmsNotificationBeforePickupMessageTemplate: {
            type: 'keyString',
            validationRegex: Validators.smsRegexString,
            displayName: 'Second SMS Message before order pickup time',
            conditions: [
                {
                    field: 'futureOrdersEnabled',
                    values: [true],
                },
                {
                    field: 'smsNotificationEnabled',
                    values: [true],
                },
                {
                    field: 'futureOrdersSecondSmsNotificationBeforePickupEnabled',
                    values: [true],
                },
            ],
            tooltip: {
                text: 'Consult eng for help setting this up. Available tokens: GUEST_NAME, ORDER_NUMBER, LOCATION_NAME, ORG_NAME, ORDER_PICKUP_TIME, OUTPOST_NAME, KIOSK_NAME',
            },
        },
    },
};
//# sourceMappingURL=dining_option_schema.js.map